import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledCountryCodeSelectField from "../../components/controlledComponents/ControlledCountryCodeSelectField.component";
import {
  TSpoc,
  TSpocStatus,
} from "../../../apis/spocPremise/spocPremise.interfaces";
import {
  useCreateSpocHook,
  useUpdateSpocHook,
} from "../../../hooks/useSpocHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

// 10-digit national mobile number (the country code is captured separately).
const phoneNumberRegex = /^[6-9]\d{9}$/;

/** Split a stored phone string (e.g. "+919876543210") into a dial code + national number. */
const splitPhone = (
  phone: string,
): { countryCode: string; phoneNumber: string } => {
  const cleaned = (phone ?? "").replace(/[^\d+]/g, "");
  const phoneNumber = cleaned.replace(/^\+/, "").slice(-10);
  const prefix = cleaned.replace(/^\+/, "").slice(0, -10).replace(/^0+/, "");
  return {
    countryCode: prefix ? `+${prefix}` : "+91",
    phoneNumber,
  };
};

const spocSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must not exceed 100 characters"),
  role: z
    .string()
    .min(1, "Role/Title is required")
    .min(2, "Role/Title must be at least 2 characters")
    .max(100, "Role/Title must not exceed 100 characters"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  countryCode: z.string().min(1, "Country code is required"),
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(phoneNumberRegex, "Please enter a valid 10-digit phone number"),
  status: z.enum(["primary", "secondary"], {
    errorMap: () => ({ message: "Please select a valid designation" }),
  }),
});

type SpocFormData = z.infer<typeof spocSchema>;

interface ManageSpocDialogProps {
  open: boolean;
  onClose: () => void;
  spoc?: TSpoc | null;
}

const ManageSpocDialog: FC<ManageSpocDialogProps> = ({
  open,
  onClose,
  spoc,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createSpocMutation = useCreateSpocHook();
  const updateSpocMutation = useUpdateSpocHook();
  const isEditMode = !!spoc;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SpocFormData>({
    resolver: zodResolver(spocSchema),
    defaultValues: {
      name: "",
      role: "",
      email: "",
      countryCode: "+91",
      phoneNumber: "",
      status: "secondary",
    },
  });

  useEffect(() => {
    if (spoc) {
      const { countryCode, phoneNumber } = splitPhone(spoc.phone);
      reset({
        name: spoc.name,
        role: spoc.role,
        email: spoc.email,
        countryCode,
        phoneNumber,
        status: spoc.status,
      });
    } else {
      reset({
        name: "",
        role: "",
        email: "",
        countryCode: "+91",
        phoneNumber: "",
        status: "secondary",
      });
    }
  }, [spoc, reset, open]);

  const onSubmit = async (data: SpocFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    const phone = `${data.countryCode}${data.phoneNumber}`;

    try {
      if (isEditMode && spoc) {
        await updateSpocMutation.mutateAsync({
          id: spoc.id,
          request: {
            name: data.name,
            role: data.role,
            email: data.email,
            phone,
            status: data.status,
          },
        });
        logger.show_success("SPOC updated successfully!");
      } else {
        await createSpocMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          name: data.name,
          role: data.role,
          email: data.email,
          phone,
          status: data.status,
        });
        logger.show_success("SPOC created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} SPOC. Please try again.`,
      );
    }
  };

  const statusOptions: { key: TSpocStatus; value: string }[] = [
    { key: "primary", value: "Primary" },
    { key: "secondary", value: "Secondary" },
  ];

  const isLoading =
    isSubmitting ||
    createSpocMutation.isPending ||
    updateSpocMutation.isPending;
  const isError = createSpocMutation.isError || updateSpocMutation.isError;
  const errorMessage =
    createSpocMutation.error?.message || updateSpocMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-spoc-dialog"
    >
      <DialogTitle data-testid="manage-spoc-dialog-title">
        {isEditMode ? "Edit SPOC" : "Add New SPOC"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="spoc-form">
        <DialogContent data-testid="spoc-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="spoc-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} SPOC. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={2}
            sx={{ mt: 1 }}
            data-testid="spoc-form-container"
          >
            <Grid item xs={3}>
              <ControlledTextField
                control={control as any}
                name="name"
                id="spoc-name"
                label="Name"
                required
                errorMessage={errors.name?.message}
                isGridItem={false}
                fullWidth={true}
              />
            </Grid>

            <Grid item xs={3}>
              <ControlledTextField
                control={control as any}
                name="role"
                id="spoc-role"
                label="Role/Title"
                required
                errorMessage={errors.role?.message}
                isGridItem={false}
                fullWidth={true}
              />
            </Grid>

            <Grid item xs={6}>
              <ControlledTextField
                control={control as any}
                name="email"
                id="spoc-email"
                label="Email"
                type="email"
                required
                errorMessage={errors.email?.message}
                isGridItem={false}
                fullWidth={true}
              />
            </Grid>

            <Grid item xs={3}>
              <ControlledCountryCodeSelectField
                control={control}
                name="countryCode"
                id="spoc-phone-country-code"
                label="Code"
                isRequired
                errorMessage={errors.countryCode?.message}
                isGridItem={false}
                dataTestId="spoc-phone-country-code"
              />
            </Grid>

            <Grid item xs={3}>
              <ControlledTextField
                control={control as any}
                name="phoneNumber"
                id="spoc-phone"
                label="Phone"
                required
                errorMessage={errors.phoneNumber?.message}
                isGridItem={false}
                fullWidth={true}
              />
            </Grid>

            <Grid item xs={3}>
              <ControlledSelectField
                control={control as any}
                name="status"
                id="spoc-status"
                label="Designation"
                itemList={statusOptions}
                isRequired
                errorMessage={errors.status?.message}
                isGridItem={false}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions data-testid="spoc-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-spoc-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-spoc-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update SPOC"
                : "Create SPOC"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageSpocDialog;
